// src/components/practice/tasks/MatchingStatementsToSpeakers.jsx
import React from 'react';
import { marked } from 'marked';
import { FlagButton } from '../PartDisplay';
import MatchingQuestion from '../MatchingQuestion';
import { X } from 'lucide-react';

/**
 * DEMO VERSION - With Headshot Photos for Each Person
 * Automatically detects gender from names and uses appropriate stock photos
 */
const MatchingStatementsToSpeakers = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleBatchAnswerChange,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  ...props
}) => {
  let mqQuestions = [];
  let mqOptions = [];
  let leftColumnExtras = null;
  let answerSheetTitle = 'Antwortbogen';
  let answerSheetSubtitle = 'Markieren Sie Ihre Antwort.';

  // --- MOVED FROM BOTTOM ---
  const isAudioTask = partData.taskType?.includes('conversation-comprehension') ||
                      partData.taskType?.includes('discussion-comprehension') ||
      partData.taskType?.includes('podcast-comprehension') ||
                      partData.taskType?.includes('monologue-comprehension');
  // --- END MOVE ---

  const realAnswers = Object.values(partAnswers);
  const exampleAnswers = examples.map(ex => ex.correctAnswer);
  const usedOptionIds = new Set([...realAnswers, ...exampleAnswers]);

  const allowMultipleMatches = !(partData.taskType === 'matching-opinions' && partData.content?.options);
  const answersToDisable = allowMultipleMatches ? [] : exampleAnswers;

  // Function to detect gender from German names
  const detectGender = (name) => {
    const maleName = ['erik', 'nils', 'thomas', 'max', 'peter', 'paul', 'hans', 'michael', 'stefan', 'markus', 'jan', 'lukas', 'felix', 'jonas', 'leon', 'tim', 'tom', 'ben', 'david'];
    const femaleName = ['katharina', 'franzi', 'anna', 'maria', 'laura', 'lisa', 'sarah', 'julia', 'sophie', 'lena', 'emma', 'mia', 'hannah', 'lea', 'marie', 'paula', 'clara', 'franziska'];

    const lowerName = name.toLowerCase();

    if (maleName.includes(lowerName)) return 'male';
    if (femaleName.includes(lowerName)) return 'female';

    // Default fallback - check ending patterns
    if (lowerName.endsWith('a') || lowerName.endsWith('e')) return 'female';
    return 'male';
  };

  // Function to get appropriate photo URL based on gender
  const getPhotoUrl = (name, index) => {
    const gender = detectGender(name);
    // Use randomuser.me API for gender-specific portraits
    // Numbers 1-99 for variety
    const photoNumber = (index % 99) + 1;
    return `https://randomuser.me/api/portraits/${gender === 'female' ? 'women' : 'men'}/${photoNumber}.jpg`;
  };

  // --- Logic to configure the layout based on taskType ---
  if (partData.taskType === 'matching-opinions' && partData.content?.options) {
    // --- Case 1: "matching-opinions" (e.g., speakers) ---
    const statements = partData.content.options;
    mqQuestions = questions.map(q => ({
      questionId: q.questionId,
      number: q.questionNumber,
      text: `Sprecher ${q.questionNumber}`,
    }));
    mqOptions = statements;
    answerSheetTitle = 'Sprecher zuordnen';
    answerSheetSubtitle = 'Welcher Sprecher sagt welche Aussage?';

    leftColumnExtras = (
      <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
        <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-4">
          Aussagen
        </h3>
        <ul className="list-none space-y-2">
          {statements.map(opt => {
            const isUsed = usedOptionIds.has(opt.id);
            return (
              <li key={opt.id} className="flex items-start">
                <span className="font-bold mr-2 flex-shrink-0">{opt.id.toUpperCase()})</span>
                <span className={`text-gray-700 ${isUsed ? 'line-through text-gray-400' : ''}`}>
                  {opt.text}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );

  } else {
    // --- Case 2: All other types (opinion-matching-forum, listening tasks, etc.) ---
    let options = [];
    if ((partData.taskType === 'opinion-matching-forum' || partData.taskType === 'opinion-matching-academic') && partData.content?.texts) {
        options = partData.content.texts.map(p => ({ id: p.id, text: `${p.id.toUpperCase()}. ${p.title || p.author}` }));
    } else if (partData.taskType === 'podcast-comprehension' && partData.content?.options) {
        options = partData.content.options;
    } else if (partData.taskType === 'discussion-comprehension' && questions?.[0]?.options) {
        // MODIFIED: Include speaker name with letter for discussion-comprehension
        options = questions[0].options.map(opt => ({
          id: opt.id,
          text: `${opt.id.toUpperCase()} - ${opt.text}`
        }));
    } else if (questions?.[0]?.speakers) {
        options = questions[0].speakers.map(s => ({ id: s, text: s }));
    }
    if (partData.partId === 'reading-part-4') {
      options.push({ id: '0', text: '0' });
    }

    mqQuestions = questions.map(q => ({
      questionId: q.questionId,
      number: q.questionNumber,
      text: q.questionText,
    }));
    mqOptions = options;
    answerSheetTitle = 'Aussagen zuordnen';
    answerSheetSubtitle = 'Welche Aussage passt zu wem?';

    // MODIFIED: Add photos to reading texts
    const showSpeakerTexts = partData.taskType.includes('opinion-matching');
    if (showSpeakerTexts) {
      leftColumnExtras = (
        <div className="bg-white p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-6">
            Personen / Meinungen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(partData.content.texts || []).filter(p => !p.isExample).map((person, index) => {
              const isUsed = exampleAnswers.includes(person.id);
              const personName = person.title || person.author;

              // --- MODIFIED LOGIC ---
              // Only show photos if it's NOT the academic task
              const showPhoto = partData.taskType !== 'opinion-matching-academic';
              const photoUrl = showPhoto ? getPhotoUrl(personName, index) : null;
              // --- END MODIFICATION ---

              return (
                <div key={person.id} className="bg-white border-2 border-gray-300 p-5 h-full relative">
                  {/* Letter Badge - Top Right Corner */}
                  <div className="absolute top-3 right-3 w-10 h-10 bg-gray-900 text-white font-bold flex items-center justify-center text-lg border-2 border-gray-900">
                    {person.id.toUpperCase()}
                  </div>

                  {/* Person Header with Photo */}
                  <div className="flex items-center mb-4 pb-4 border-b border-gray-200">

                    {/* --- ADDED WRAPPER --- */}
                    {showPhoto && (
                      <img
                        src={photoUrl}
                        alt={personName}
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-400 mr-4"
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {/* --- END WRAPPER --- */}

                    {/* Person Name Only (no letter) */}
                    <div>
                      <p className={`font-bold text-xl text-gray-900 ${isUsed ? 'line-through' : ''}`}>
                        {personName}
                      </p>
                    </div>
                  </div>

                  {/* Person's Opinion/Content */}
                  <div
                    className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: marked.parse(person.content) }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }

  // --- leftContent ---
  const leftContent = (
    <div className="space-y-6">
      {/* Context text for listening (if exists) */}
      {partData.content?.mainText && (
        <div className="border-2 border-gray-900 bg-blue-50 p-4 rounded-none">
          <div
            className="text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: marked.parse(partData.content.mainText) }}
          />
        </div>
      )}

      {/* Reading Texts */}
      {leftColumnExtras}
    </div>
  );

  // --- rightContent ---
  const rightContent = (
    <div>
      {/* Header */}
    <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
      <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
        {answerSheetTitle}
      </h3>
      <p className="text-xs text-gray-600 mt-1">
        {answerSheetSubtitle}
      </p>
    </div>

    {/* --- MODIFIED CONDITION: Added isAudioTask --- */}
    {partData.taskType === 'discussion-comprehension' && mqOptions.length > 0 && mqOptions.length <= 5 && (
      <div className="border-2 border-gray-900 bg-white p-4 rounded-none mb-4">
        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
          Wer sagt das?
        </h4>
        {/* Vertical stack of boxes */}
        <div className="grid grid-cols-3 gap-3">
          {mqOptions.map((opt, index) => {
            // Extract name from text like "A - Moderator" or "B - Frau Gerster, Studentin"
            const fullText = opt.text.includes(' - ') ? opt.text.split(' - ')[1] : opt.text;
            // Get first name for gender detection
            const nameForPhoto = fullText.split(',')[0].replace('Frau ', '').replace('Herr ', '').trim();
            const photoUrl = getPhotoUrl(nameForPhoto, index);

            return (
              <div key={opt.id} className="flex flex-col items-center p-3 bg-gray-50 rounded border border-gray-300">
                {/* Circular photo on top */}
                <img
                  src={photoUrl}
                  alt={fullText}
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-400 mb-2"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Name text below */}
                <span className="text-sm font-semibold text-gray-900 text-center">{opt.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    )}
    {/* Main Answer Box */}

      {/* Main Answer Box */}
      <div className="border-2 border-gray-900 bg-white rounded-none">
        {/* Examples */}
        {examples.map((ex, idx) => {
          const exampleText = ex.questionText || ex.statement || "Beispiel-Aussage";
          const correctAdText = ex.correctAnswer ? ex.correctAnswer.toUpperCase() : '?';

          return (
            <div
              key={`ex-${idx}`}
              className="bg-yellow-50 p-4 opacity-90 border-b border-gray-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 flex items-start">
                  <span className="font-bold text-sm mr-2 pt-0.5">
                    {ex.questionNumber || '0'}.
                  </span>
                  <span className="text-sm leading-relaxed">
                    {exampleText}
                  </span>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <div
                    className="border-2 border-gray-900 bg-gray-900 text-white font-bold text-sm px-3 py-1.5 rounded-none cursor-not-allowed"
                    style={{ minWidth: '70px', textAlign: 'center' }}
                  >
                    {correctAdText}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Regular Questions */}
        <div className="p-4">
          <MatchingQuestion
            questions={mqQuestions}
            options={mqOptions}
            savedAnswers={partAnswers}
            onAnswerChange={handleBatchAnswerChange}
            title="Option wählen"
            flaggedQuestions={flaggedQuestions}
            toggleFlagQuestion={toggleFlagQuestion}
            exampleAnswers={answersToDisable}
            allowMultipleMatches={allowMultipleMatches}
            showFullTextInButtons={partData.taskType === 'discussion-comprehension' || partData.taskType === 'podcast-comprehension'}
/>
        </div>
      </div>
    </div>
  );

  const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      {isAudioTask ? (
        <div className="max-w-4xl mx-auto">
          {leftContent}
          <div className="mt-6 hidden md:block">
            {rightContent}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">
          <div className="lg:col-span-3 min-w-0">
            {leftContent}
          </div>
          <div className="hidden lg:block lg:col-span-2 mt-6 lg:mt-0">
            <div className="lg:sticky lg:top-6">
              {rightContent}
            </div>
          </div>
        </div>
      )}
      {mobileOverlay}
    </div>
  );
};

export default MatchingStatementsToSpeakers;